import React, { useEffect, useState } from 'react'
import { matchPath, useMatch } from 'react-router'
import { useLocalStorage } from 'usehooks-ts'

import { useSession } from '../../../providers/SessionProvider'
import { GroupHeading } from './GroupHeading'
import { GroupContent } from './GroupContent'
import { Links } from './Links'
import { HoverMenu } from './HoverMenu'
import { getLinks } from './utils'
import { NavToggle } from './NavToggle'
import { NAV_WIDTH_EXPANDED, NAV_WIDTH_COLLAPSED, NAV_TRANSITION_DURATION } from './secondary-nav-config'

import styles from './Secondary.module.css'
import navGroupStyles from './NavGroup.module.css'
import hoverStyles from './HoverMenu.module.css'

// Constants for localStorage keys
const NAV_EXPANDED_KEY = 'calibre-nav-expanded'

export interface SecondaryNavGroup {
  children: SecondaryNavGroupItem[]
  icon: React.ComponentType
  labelId: string
}
export interface SecondaryNavGroupItem {
  exactMatch?: boolean
  labelId: string
  permission?: string
  highlightMatches?: string[]
  to: (params: {
    orgId?: string | null
    siteId?: string
    teamId?: string
    uuid?: string
    shareToken?: string
  }) => string
}

export interface SecondaryNavigationProps {
  forceRefresh?: boolean
  orgId?: string | null
  siteId?: string
  teamId?: string
  uuid?: string
  shareToken?: string
}

const SecondaryNavigation = ({
  forceRefresh,
  orgId,
  teamId,
  siteId,
  uuid,
  shareToken
}: SecondaryNavigationProps): JSX.Element | null => {
  // Check for setup page early before any state updates or effects
  const matchSetup = useMatch({ path: '/setup/:orgId?', caseSensitive: false })

  const { can, organisation, error, team, currentUser } = useSession({
    orgId,
    teamId
  })

  const matchOrganisation = useMatch({ path: '/organisations/:orgId/*' })
  const matchUser = useMatch({ path: '/you/*' })
  const matchSinglePageTest = useMatch({ path: '/tests/:uuid/:shareToken/*', end: true, caseSensitive: false })

  // @ts-ignore-next-line
  const userIsCustomer = currentUser?.memberships?.map(m => m.organisation.isActive).includes(true) || false

  const links = getLinks({
    matchUser: !!matchUser,
    singlePageTest: !!matchSinglePageTest,
    orgId: organisation?.slug,
    siteId,
    teamId,
    userIsCustomer
  })

  // Using useLocalStorage hook to persist navigation state (only for expanded/collapsed state)
  const [navExpanded, setNavExpanded] = useLocalStorage(NAV_EXPANDED_KEY, true)

  // Use React state for section expanded state (not persisted to localStorage)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})

  // Regular state for UI control
  const [showExpandedNavContents, setShowExpandedNavContent] = useState(true)
  const [loaderRemoved, setLoaderRemoved] = useState(false)

  // Update expanded sections based on active route
  useEffect(() => {
    // Skip effect for setup pages
    if (matchSetup) return

    const newExpandedSections: Record<string, boolean> = {}

    links.forEach(({ labelId, children }) => {
      // Check if this section contains an active item
      const hasActiveChild = children.find(({ to, highlightMatches }) => {
        if (highlightMatches) {
          const activeMatches = highlightMatches.filter(match => {
            return matchPath({ path: match }, location.pathname)
          })

          if (activeMatches.length > 0) {
            return true
          }
        }

        // Check direct path match
        return matchPath({ path: to({ orgId, siteId, teamId }) }, location.pathname)
      })

      // Maintain previous expanded state for sections without active items
      // Always ensure the section with the active item is expanded
      if (hasActiveChild) {
        newExpandedSections[labelId] = true
      } else {
        // Keep existing state for non-active sections
        newExpandedSections[labelId] = expandedSections[labelId] || false
      }
    })

    setExpandedSections(newExpandedSections)
  }, [orgId, siteId, teamId, location.pathname, links, matchSetup])

  useEffect(() => {
    // Skip effect for setup pages
    if (matchSetup) return

    let timeoutId: ReturnType<typeof setTimeout>
    if (navExpanded) {
      document.documentElement.style.setProperty('--secondary-nav-width', NAV_WIDTH_EXPANDED)
      timeoutId = setTimeout(() => {
        setShowExpandedNavContent(true)
      }, NAV_TRANSITION_DURATION)
    } else {
      document.documentElement.style.setProperty('--secondary-nav-width', NAV_WIDTH_COLLAPSED)
      removeLoader()
      setShowExpandedNavContent(false)
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [navExpanded, matchSetup])

  const removeLoader = () => {
    if (!loaderRemoved) {
      setLoaderRemoved(true)
      const pageLoader = document.querySelector('.page-loader')
      if (pageLoader) {
        pageLoader.remove()
      }
    }
  }

  const toggleSection = (labelId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [labelId]: !prev[labelId]
    }))
  }

  // Custom navToggle handler to ensure we update localStorage
  const handleNavToggle = (expanded: boolean | ((prev: boolean) => boolean)) => {
    setNavExpanded(expanded)
  }

  // Early return for setup pages after all hooks have been called
  if (matchSetup) return null

  if ((!!matchOrganisation && !!matchUser && !!matchSinglePageTest && !organisation) || error?.type == 'Organisation') {
    return null
  }

  return (
    <nav
      aria-label="Secondary"
      data-qa="secondaryNavigation"
      className={`${styles.secondary} ${!navExpanded ? navGroupStyles.navCollapsed : ''}`}
    >
      <h2 className={styles.visuallyHidden}>Secondary navigation</h2>
      {links.map(({ labelId, icon, children }) => {
        const hasActiveChild = children.find(({ to, highlightMatches }) => {
          if (highlightMatches) {
            const activeMatches = highlightMatches.filter(match => {
              return matchPath({ path: match }, location.pathname)
            })

            if (activeMatches.length > 0) {
              return true
            }
          }

          return matchPath({ path: to({ orgId, siteId, teamId }) }, location.pathname)
        })

        // Check if any of the children are visible. If not, don't render the group.
        if (children.filter(({ permission }) => (permission ? can(permission) : true)).length == 0) return null

        return (
          <div key={labelId} className={`${styles.groupWrapper} ${!navExpanded ? hoverStyles.hoverMenuWrapper : ''}`}>
            <GroupHeading
              hasActiveChild={!!hasActiveChild}
              icon={icon}
              labelId={labelId}
              navExpanded={navExpanded}
              showExpandedNavContents={showExpandedNavContents}
              teamName={team?.name}
              to={children[0].to({ orgId: organisation?.slug, siteId, teamId, uuid, shareToken })}
              isExpanded={expandedSections[labelId]}
              onToggle={() => toggleSection(labelId)}
            />
            <GroupContent labelId={labelId} isExpanded={!navExpanded || expandedSections[labelId]}>
              <Links
                can={can}
                forceRefresh={forceRefresh}
                links={children}
                orgId={organisation?.slug}
                siteId={siteId}
                teamId={teamId}
                uuid={uuid}
                shareToken={shareToken}
              />
            </GroupContent>
            {!navExpanded && (
              <HoverMenu
                labelId={labelId}
                teamName={team?.name}
                links={children}
                can={can}
                forceRefresh={forceRefresh}
                orgId={organisation?.slug}
                siteId={siteId}
                teamId={teamId}
                uuid={uuid}
                shareToken={shareToken}
              />
            )}
          </div>
        )
      })}
      <NavToggle navExpanded={navExpanded} setNavExpanded={handleNavToggle} />
    </nav>
  )
}

export default SecondaryNavigation
